/**
 * FloatingScreenshotButton - Floating buttons for screenshot capture and download
 *
 * Two buttons:
 * 1. Camera (Capture): Captures screenshot and saves to backend (requires saved project)
 * 2. Download: Captures and downloads locally as PNG file
 */

import React, { useState } from 'react';
import { Camera, Download, Check, Loader2, Save } from 'lucide-react';

interface FloatingScreenshotButtonProps {
  /** Capture and save screenshot to backend */
  onCaptureAndSave: () => Promise<{ success: boolean; error?: string }>;
  /** Capture and download screenshot locally */
  onCaptureAndDownload: () => Promise<{ success: boolean; error?: string }>;
  /** Whether project has been saved (required for backend save) */
  hasProjectId: boolean;
  /** Callback to trigger project save */
  onRequestSave?: () => void;
  disabled?: boolean;
}

export function FloatingScreenshotButton({
  onCaptureAndSave,
  onCaptureAndDownload,
  hasProjectId,
  onRequestSave,
  disabled = false,
}: FloatingScreenshotButtonProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);

  const handleSave = async () => {
    if (isSaving || disabled) return;

    // If no project saved, prompt to save first
    if (!hasProjectId) {
      if (onRequestSave) {
        alert('Please save the project first before capturing a screenshot.');
        onRequestSave();
      }
      return;
    }

    setIsSaving(true);
    try {
      const result = await onCaptureAndSave();
      if (result.success) {
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
      } else {
        console.error('Screenshot save failed:', result.error);
        alert(`Screenshot save failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Screenshot save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading || disabled) return;

    setIsDownloading(true);
    try {
      const result = await onCaptureAndDownload();
      if (result.success) {
        setShowDownloadSuccess(true);
        setTimeout(() => setShowDownloadSuccess(false), 2000);
      } else {
        console.error('Screenshot download failed:', result.error);
      }
    } catch (error) {
      console.error('Screenshot download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="absolute bottom-4 left-20 z-[1000] flex gap-2">
      {/* Save to Backend Button */}
      <div className="group relative">
        <button
          onClick={handleSave}
          disabled={isSaving || disabled}
          className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-lg transition-all ${
            isSaving
              ? 'bg-blue-400 text-white cursor-wait'
              : showSaveSuccess
              ? 'bg-green-500 text-white'
              : !hasProjectId
              ? 'bg-gray-200 text-gray-400 border-2 border-gray-300'
              : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-blue-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={hasProjectId ? 'Save Screenshot to Project' : 'Save project first'}
        >
          {isSaving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : showSaveSuccess ? (
            <Check className="h-5 w-5" />
          ) : (
            <Camera className="h-5 w-5" />
          )}
        </button>
        {/* Tooltip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-14 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {isSaving ? 'Saving...' : showSaveSuccess ? 'Saved!' : hasProjectId ? 'Save to Project' : 'Save project first'}
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="group relative">
        <button
          onClick={handleDownload}
          disabled={isDownloading || disabled}
          className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-lg transition-all ${
            isDownloading
              ? 'bg-blue-400 text-white cursor-wait'
              : showDownloadSuccess
              ? 'bg-green-500 text-white'
              : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-green-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Download Screenshot"
        >
          {isDownloading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : showDownloadSuccess ? (
            <Check className="h-5 w-5" />
          ) : (
            <Download className="h-5 w-5" />
          )}
        </button>
        {/* Tooltip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-14 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {isDownloading ? 'Capturing...' : showDownloadSuccess ? 'Downloaded!' : 'Download PNG'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FloatingScreenshotButton;
